import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { AlertController } from '@ionic/angular/lazy';
import { PermissionService } from 'src/app/services/permission.service';

// Blocks navigation to routes the current user doesn't have permission to access.

// Redirect behaviour:
//   - In-app navigation (e.g. person page → orders): redirects back to the current page immediately,
//     so the transition feels instant with no visible flash of the blocked page.
//   - Direct URL entry (router.url is '/'): waits for the user to dismiss the alert before
//     redirecting to /secure/ (which StartPageGuard resolves to the appropriate start page),
//     so they aren't abruptly moved mid-read.

@Injectable()
export class PermissionGuard {

    constructor(
        private router: Router,
        private permissionService: PermissionService,
        private alertController: AlertController,
    ) { }

    async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {

        await this.permissionService.loadCache();

        const key = route.data['permission'];
        if (this.permissionService.isPermitted(key)) {
            return true;
        }

        // Capture the current URL before any navigation occurs.
        // If it's '/', the user arrived via direct URL entry with no in-app history.
        const currentUrl = this.router.url;
        const isDirectEntry = currentUrl === '/';

        const alert = await this.alertController.create({
            header: 'Insufficient permissions',
            message: route.data['permissionMessage'] ?? "You don't have permission to view this page.",
            buttons: ['OK'],
        });
        await alert.present();

        if (isDirectEntry) {
            // Wait for dismissal before redirecting so the alert isn't pulled away mid-read.
            await alert.onDidDismiss();
        }

        this.router.navigate([isDirectEntry ? '/secure/' : currentUrl], { replaceUrl: true });
        return false;
    }

}
